import { ContactDataType, iContactData } from "../../../../interfaces/Contact";
import { isEmail } from "../../../../interfaces/emailModel";
import iEmailAddress from "../../../../interfaces/iEmailAddress";
import { iCommunication, iEnquiry } from "../../../../interfaces/iEnquiry";
import { Wizard } from "../../../../state/workflowSlice";
import store from "../../../../state/store";
import { CallEmsApi, iEmsApiResponse, iEmsApiResponseBody } from "../../../../Logic/EmsAPI/FetchExtension";
import {
    getLocalValue,
    getSessionValue,
    saveLocalValue,
    saveSessionValue,
} from "../../../../utilities/localStore/calls";
import { iAuth, IdentityProvider, iToken } from "../../../../interfaces/auth";
import { logErrorToService } from "../../../../error-boundary/logerror";

export function getEnquiryContacts(
    enquiry: iEnquiry,
    wizard: Wizard,
    communications: iCommunication[],
): iEmailAddress[] {
    let recents: iEmailAddress[] = [];
    const currentUser = store.getState().UserInformation;
    if (isEmail(wizard.workflow.data.EnquirerDetails.Email)) {
        recents.push({
            name: wizard.workflow.data.EnquirerDetails.FullName,
            address: wizard.workflow.data.EnquirerDetails.Email,
        });
    } else if (isEmail(enquiry.enquirerEmail)) {
        recents.push({
            name: enquiry.enquirerName,
            address: enquiry.enquirerEmail,
        });
    }

    for (let i = 0; i < communications.length; i++) {
        const from: iEmailAddress | undefined = communications[i].email?.from;
        const tos: iEmailAddress[] | undefined = communications[i].email?.to;
        const ccs: iEmailAddress[] | undefined = communications[i].email?.cc;
        const bccs: iEmailAddress[] | undefined = communications[i].email?.bcc;
        if (
            from !== undefined &&
            recents.find(t => {
                return t.address.toLowerCase() === from.address.toLowerCase();
            }) === undefined
        ) {
            recents.push(from);
        }
        tos?.forEach(ct => {
            if (recents.find(t => t.address.toLowerCase() === ct.address.toLowerCase()) === undefined) recents.push(ct);
        });
        ccs?.forEach(ct => {
            if (recents.find(t => t.address.toLowerCase() === ct.address.toLowerCase()) === undefined) recents.push(ct);
        });
        bccs?.forEach(ct => {
            if (recents.find(t => t.address.toLowerCase() === ct.address.toLowerCase()) === undefined) recents.push(ct);
        });
    }
    const mailboxinfo = currentUser.mailboxes.find(x => x.id == enquiry.mailbox);
    if (mailboxinfo) recents = recents.filter(x => x.address != mailboxinfo.emailAddress);
    return recents;
}

export async function getResults(
    query: string,
    contacts: iContactData[],
    addedContacts: iEmailAddress[] | undefined,
): Promise<iContactData[]> {
    if (addedContacts) {
        contacts = contacts.filter(x => x.address != addedContacts.find(a => a.address == x.address)?.address);
    }

    if (query === "") {
        return contacts.filter(x => x.contactType == ContactDataType.Enquiry).slice(undefined);
    }

    // match and merge
    const matches: iContactData[] = [];
    const nonMatches: iContactData[] = [];

    contacts.forEach(ct => {
        if (ct.name && ct.name.toLowerCase().includes(query.toLowerCase())) {
            matches.push(ct);
        } else if (ct.address.toLowerCase().includes(query.toLowerCase())) {
            matches.push(ct);
        } else {
            nonMatches.push(ct);
        }
    });

    if (query.length >= 3) {
        const globalAddressBookMatches = await GlobalAddressBookSearch(query);

        const globalAddressBookMapped = globalAddressBookMatches.map((e: globalAddressContacts) => ({
            name: e.displayName,
            address: e.email,
            contactType: ContactDataType.GlobalAddressBook,
        })) as iContactData[];

        matches.push(...globalAddressBookMapped);

        const tenantContactMatches = await GetContacts(query);

        if (tenantContactMatches.data !== undefined) {
            const tenantContactMapped = tenantContactMatches.data?.items.map((e: Contact) => ({
                name: e.name,
                address: e.address,
                contactType: ContactDataType.TenantContacts,
            })) as iContactData[];

            matches.push(...tenantContactMapped);
        }
    }

    // sort by strong
    const sortedMatches: iContactData[] = matches.sort((a: iContactData, b: iContactData) => {
        const aNameStarts = a.name?.startsWith(query);
        const bNameStarts = b.name?.startsWith(query);
        const aAddStarts = a.address.startsWith(query);
        const bAddStarts = b.address.startsWith(query);

        return aNameStarts || aAddStarts ? -1 : bNameStarts || bAddStarts ? 1 : 0;
    });

    if (matches.length == 0) {
        let results: iContactData[] = [...sortedMatches].slice(undefined);
        return results;
    } else {
        const uniqueMatches = (contacts1: iContactData[]): iContactData[] => {
            const emailMap = new Map<string, iContactData>();

            contacts1.forEach(contact => {
                const existing = emailMap.get(contact.address);
                if (!existing || getPriority(contact) < getPriority(existing)) {
                    emailMap.set(contact.address, contact);
                }
            });

            return Array.from(emailMap.values());
        };

        const PrioritiseMatches = [...uniqueMatches(matches)].sort((a, b) => getPriority(a) - getPriority(b));

        let results: iContactData[] = [...PrioritiseMatches].slice(undefined);
        return results;
    }
}

const getPriority = (contact: iContactData): number => {
    if (contact.contactType == ContactDataType.Enquiry) return 1;
    if (contact.contactType == ContactDataType.MyContacts) return 2;
    if (contact.contactType == ContactDataType.GlobalAddressBook) return 3;
    if (contact.contactType == ContactDataType.TenantContacts) return 4;
    return 5;
};

interface globalAddressContacts {
    displayName: string;
    email: string;
    personType?: string;
}

async function GlobalAddressBookSearch(query: string): Promise<Array<globalAddressContacts>> {
    const crypto = getLocalValue<iAuth>("auth");
    if (crypto) if (crypto.type !== IdentityProvider.MsEntra && !crypto.integratedGalEnabled) return [];

    const graphToken = getLocalValue<iToken>("msGraphToken");

    try {
        const cachedGALContacts = getSessionValue<Array<globalAddressContacts>>("cachedGALContacts-" + query);

        if (cachedGALContacts) return cachedGALContacts;

        const body = {
            requests: [
                {
                    entityTypes: ["person"],
                    query: {
                        queryString: query,
                    },
                },
            ],
        };

        const response = await fetch("https://graph.microsoft.com/v1.0/search/query", {
            headers: {
                Authorization: `Bearer ${graphToken?.access_token}`,
                "Content-Type": "application/json",
            },
            method: "Post",
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            logErrorToService(new Error("Error fetching email addresses:" + response.status));
            return [];
        }

        const data = await response.json();

        const hits = data.value?.[0]?.hitsContainers?.[0]?.hits || [];

        // Convert to an array of simplified objects
        let peopleObject = hits.map((hit: any) => {
            let filteredPeople = hit.resource;

            return {
                displayName: filteredPeople.displayName,
                email: filteredPeople.userPrincipalName || "No email",
                personType: filteredPeople.personType.subclass || "",
            };
        });

        const distributionList = await DistributionListAddressBookSearch(query);
        peopleObject.push(...distributionList);

        const mailboxList = await MailboxAddressBookSearch(query);
        peopleObject.push(...mailboxList);

        if (peopleObject) {
            let result = peopleObject.filter(
                (x: globalAddressContacts) =>
                    x.personType == "OrganizationContact" || x.personType == "OrganizationUser" || x.personType == null,
            );
            const FinalResult = result.filter(
                (x: globalAddressContacts) =>
                    x.email?.toLowerCase().includes(query.toLowerCase()) ||
                    x.displayName?.toLowerCase().includes(query.toLowerCase()),
            );

            if (FinalResult) {
                if (FinalResult.length > 0) saveSessionValue("cachedGALContacts-" + query, FinalResult);
                return FinalResult;
            }
        }
    } catch (error) {
        logErrorToService(new Error("Error fetching email addresses:" + error));
    }
    return [];
}
async function DistributionListAddressBookSearch(query: string): Promise<Array<globalAddressContacts>> {
    try {
        const graphToken = getLocalValue<iToken>("msGraphToken");
        const updatedquery = encodeURIComponent(query).replace("'", "''");
        const response = await fetch(
            "https://graph.microsoft.com/v1.0/groups?$filter=startswith(displayName,'" +
                updatedquery +
                "')&$select=displayName,mail",
            {
                headers: {
                    Authorization: `Bearer ${graphToken?.access_token}`,
                    "Content-Type": "application/json",
                },
            },
        );

        if (!response.ok) {
            logErrorToService(new Error("Error fetching email addresses:" + response.status));
            return [];
        }

        const data = await response.json();

        if (data != null)
            return data.value
                .filter((e: any) => e.mail)
                .map((e: any) => ({
                    displayName: e.displayName,
                    email: e.mail,
                    contactType: ContactDataType.GlobalAddressBook,
                })) as globalAddressContacts[];
    } catch (error) {
        logErrorToService(new Error("Error fetching email addresses:" + error));
    }
    return [];
}
async function MailboxAddressBookSearch(query: string): Promise<Array<globalAddressContacts>> {
    try {
        const graphToken = getLocalValue<iToken>("msGraphToken");
        const updatedquery = encodeURIComponent(query).replace("'", "''");
        const response = await fetch(
            "https://graph.microsoft.com/v1.0/users?$filter=accountEnabled  eq false and givenName  eq null and startswith(displayName,'" +
                updatedquery +
                "')&$select=displayName,mail&$count=true",

            {
                headers: {
                    Authorization: `Bearer ${graphToken?.access_token}`,
                    "Content-Type": "application/json",
                    ConsistencyLevel: "eventual",
                },
            },
        );

        if (!response.ok) {
            logErrorToService(new Error("Error fetching email addresses:" + response.status));
            return [];
        }

        const data = await response.json();
        if (data != null)
            return data.value.map((e: any) => ({
                displayName: e.displayName,
                email: e.mail,
                contactType: ContactDataType.GlobalAddressBook,
            })) as globalAddressContacts[];
    } catch (error) {
        logErrorToService(new Error("Error fetching email addresses:" + error));
    }
    return [];
}
export function sortContacts(contacts: iEmailAddress[]): iEmailAddress[] {
    return contacts.sort((a, b) => {
        const aVal = a.name === undefined || a.name === "" ? a.address.toLowerCase() : a.name.toLowerCase();
        const bVal = b.name === undefined || b.name === "" ? b.address.toLowerCase() : b.name.toLowerCase();
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    });
}

export interface addUserContactFields {
    name: string;
    emailAddress: string;
}

export interface addUserContactsInterface extends addUserContactFields {
    showModal: boolean;
    validationErrors: string;
    previousEmail: string;
}

export async function AddUserContact(request: addUserContactFields): Promise<iEmsApiResponse> {
    let user = store.getState().UserInformation;
    return await CallEmsApi<any>("POST", `Users/${user.userID}/Contacts`, request);
}

export async function DeleteUserContact(request: { EmailAddress: string }): Promise<iEmsApiResponse> {
    let user = store.getState().UserInformation;
    return await CallEmsApi<any>("DELETE", `Users/${user.userID}/Contacts`, request);
}
export async function GetContacts(search: string): Promise<iEmsApiResponseBody<GetContactResponse>> {
    const cachedContacts = getSessionValue<iEmsApiResponseBody<GetContactResponse>>("GetContacts-" + search);

    if (cachedContacts) return cachedContacts;

    const response = await CallEmsApi<GetContactResponse>("GET", `Contacts/Search/${encodeURIComponent(search)}`);
    if (response) {
        if (response.data) if (response.data.items.length > 0) saveSessionValue("GetContacts-" + search, response);
    }
    return response;
}
interface GetContactResponse {
    items: Contact[];
}
interface Contact {
    address: string;
    name: string;
}
